import {React, computed, observable, stringify, _, autorun, action, runInAction, computedFn} from '@api'
import {TypeDataGridProps, TypeRowSelection, TypeColumn, TypeSortInfo, TypeSingleSortInfo, TypeEditInfo} from '@inovua/reactdatagrid-enterprise/types'
import {} from '@inovua/reactdatagrid-enterprise/types'
import {CatalogItemsPanelProps} from '@modules'
import {authStore, differenceInObject, guid, recursivelyRemovePrivateFields} from '@utils'
import {OFFLINE_ID, StoresInstance} from '@stores'
import {VipLocalStore} from '@modules'
import {reaction} from 'mobx'
import {RefObject, useContext} from 'react'
import ReactDOM from 'react-dom'
import {DataGridRootStore} from './DataGridRootStore'
import {PaginationToolbar} from './PaginationToolbar'
import {BuilderCardLocalData} from '@stores/catalog/ICatalogItemLocalData'

export interface IDataGridProps extends CatalogItemsPanelProps {
  queryVariables?: any
  offlineItems?: Array<any>
}

interface iTypeEditInfoExtended extends TypeEditInfo {
  data?: any
}

export const DataGridStoreContext = React.createContext<DataGridStore>(undefined)
export const useDataGridStore = () => useContext(DataGridStoreContext)

export class DataGridStore extends VipLocalStore {
  @observable key: string = null

  constructor(stores: StoresInstance, public props: IDataGridProps, public rootStore: DataGridRootStore) {
    super(stores)
    const {catalog} = this
    if (catalog.isProductView) {
      catalog.componentStores.dataGridStore = this
    }
  }

  ITEMS_PER_QUERY = 50

  // @observable activeRowId = 0
  @observable expandedRowHeights = new Map<string, number>()
  @observable entireRowExpand = new Map<string, boolean>()
  @observable everLoaded = false
  @observable loading = false
  @observable.ref data: Array<any> = []
  @observable count: number
  @observable editValue
  @observable limit: number = this.ITEMS_PER_QUERY
  @observable skip: number = 0
  @observable.ref groupRef: RefObject<HTMLDivElement> = React.createRef()
  ref: any //ReactDataGrid
  grid_onReady = r => this.ref = r

  private prevLimit: number

  private prevSidebarData: any = {}

  private _noBackButton = () => {
    history.pushState(null, document.title, location.href)
  }

  get events(): TypeDataGridProps {
    const {renderPaginationToolbar, rowExpandChange: onRowExpandChange, onEditComplete, onRenderRow, onEditValueChange, onColumnOrderChange} = this

    return {
      renderPaginationToolbar,
      onEditComplete,
      onRenderRow,
      onEditValueChange,
      onColumnOrderChange,
      onRowExpandChange
    } as any
  }

  @action onColumnOrderChange = (columnOrder) => {
    const omitFields = ['__row-expand-column', 'images', '_id'],
      orderedFields = columnOrder.filter(o => !omitFields.includes(o)),
      {catalog, catalog: {settings: {cardViewOrderOverride}, perspectiveId}} = this
    catalog.settings.cardViewOrderOverride = Object.assign({}, cardViewOrderOverride, {[perspectiveId]: _.uniq([...columnOrder, ...(cardViewOrderOverride[perspectiveId] ?? [])])})
  }

  @computed get sortInfo(): Array<TypeSingleSortInfo> {
    return this.catalog.catalogItemsOrderBy?.map(({field, order}) => ({dir: order === 'asc' ? 1 : -1, id: field, name: field}))
  }

  @computed get selectedRows(): TypeRowSelection {
    const {catalog: {selection: {selectedIds}}} = this,
      selected = {}
    Array.from(selectedIds.values()).forEach(id => selected[id] = true)
    return selected
  }

  @computed get searchPayload() {
    const {catalog, catalog: {queryVariablesEs: queryVariables}, props: {queryVariables: qVariables}} = this,
      query = !_.isEmpty(qVariables) ? {...(_.isEmpty(qVariables.query) ? {bool: {}} : qVariables.query)}
                                     : {...(_.isEmpty(queryVariables.query) ? {bool: {}} : queryVariables.query)},
      sort = !_.isEmpty(qVariables) ? [...(_.isEmpty(qVariables.sort) ? [] : qVariables.sort)]
                                    : [...(_.isEmpty(queryVariables.sort) ? [] : queryVariables.sort)]

    return {
      query,
      sort,
      ...catalog._sourceDynamic
    }
  }

  @computed get expandedCatalogItems() {
    const {catalog: {settings: {perItem}}} = this
    return Object.keys(perItem)?.filter(item => perItem[item]?.lod == 1) ?? []
  }

  @action onSortInfoChange = (ord: TypeSortInfo) => {
    const {catalog, catalog: {groupBy: {catalogItemsGroup: {orderBy}}, _fieldByName}} = this,
      order = (ord as Array<TypeSingleSortInfo>)

    if (order.length) {
      const o = _.last(order),
        fieldDef = _fieldByName[o.id],
        index = orderBy.findIndex(f => f.field == fieldDef.field || f.field === fieldDef.label)
      this.rootStore.onClickSortMenu({
        field: catalog._fieldByName[o.id],
        custom: index !== -1 ? orderBy[index]?.custom : false,
        isProductView: true,
        direction: o.dir === 1 ? 'asc' : 'desc'
      })
    } else {
      const o = _.first(orderBy)
      o.order = o.order === 'asc' ? 'desc' : 'asc'
    }
  }

  @action runQuery = async ({skip, limit, force}) => {
    if (!authStore.userIsAuthenticated || (this.loading && !force)) return

    const {catalog, es, props: {offlineItems}} = this,
      scrollEls: NodeListOf<HTMLDivElement> = document.querySelectorAll('[data-name="scroller"]')
    this.loading = true
    // if (!this.columns.length || this.columns.length !== this.visibleFields.length) this._setColumns()
    if (skip * this.limit < this.data.length && !force) {
      return
    }
    try {
      const {settings: {showOnlyOfflineItems}, filteredOfflineItems, isProductView} = catalog

      let data, count

      if (!showOnlyOfflineItems) {
        const {searchPayload} = this,
          {index} = catalog,
          {hits: {hits, total}} = await es.search({
            meta: `DataGridStore.runQuery(${index})`,
            body: {...searchPayload, from: skip, size: (limit - (filteredOfflineItems?.length ?? 0))},
            index
          }, {
            log: {query: false, result: false}
          })

        data = [...(!skip ? [] : this.data), ...(!skip ? [...(!isProductView ? (offlineItems ?? []) : catalog.filteredOfflineItems)] : []), ...hits.map(h => ({_id: h._id, ...h._source}))]
        count = total.value
      } else {
        data = [...(!isProductView ? (offlineItems ?? []) : catalog.filteredOfflineItems)]
      }
      count = isProductView ? catalog.itemCount : (count ?? 0)
      runInAction(() => {
        this.data = data
        this.count = count
        this.everLoaded = true
        this.loading = false
      })
      return {data, count}
    } finally {
      runInAction(() => {
        this.everLoaded = true
        this.loading = false
      })
    }
  }

  @action _debounceRunQuery = _.debounce(this.runQuery, 500)

  @action private _debounceUpdateDataByIndex = _.debounce((items) => this.updateTableViewDataByIndex(items), 3000)

  @action rowStyle = ({data}) => {
    const {catalog, selectedRows, stores} = this,
      id = catalog.idFor(data),
      offline = id.startsWith(OFFLINE_ID),
      matchedPerspective = catalog.perspectives.filter(p => p.matchCatalogItem && p.matchCatalogItem(data, {stores})) ?? [],
      _selected = selectedRows[id]

    if ((offline || matchedPerspective.length) && !_selected) {
      return {
        backgroundColor: offline ? 'var(--tertiary-light-yellow)' : 'var(--tertiary-light-skyblue)'
      }
    }

    return {}
  }

  @action onRowClick = (e, rowProps) => {
    const {data} = rowProps, {catalog, catalog: {selection}} = this,
      id = catalog.idFor(data)
    selection.onClickItem(e, id, [...this.data.map(d => catalog.idFor(d))])
    catalog.focusedCatalogItemId = id
  }

  @action onRowDoubleClick = (e, rowProps) => {
    const {data} = rowProps, {stores: {catalogTab, flexLayoutCatalogRoot, flexLayout: {isMaximized}}, catalog, catalog: {sidebar: {right}}} = this,
      id = catalog.idFor(data)

    right.updateState({open: true})

    right.path = [{where: [{value: id}]}]
    catalog.focusedCatalogItemId = id
    if (catalogTab && flexLayoutCatalogRoot && !isMaximized) {
      catalogTab.tab.openCatalogItemDetailsInNewTab({path: right.path, name: ''})
    }
  }

  @action _updateNestedData = async ({rowIndex, columnId, updatedValue, prev}: { rowIndex: number, columnId: string, updatedValue?: any, prev?: any }) => {
    const {catalog} = this,
      {index: toIndex, idFor, createItem: {updateObjectInRemoteByQuery}} = catalog,
      clone = [...this.data],
      data = _.cloneDeep(clone[rowIndex]),
      id = idFor(data),
      query = `{"terms": {"exId": ${stringify([id])} }}`,
      columns = columnId.split('_'),
      fieldName = _.first(columns),
      field = catalog._fieldByName[fieldName],
      {updateOnAssignment, section} = field,
      nested = _.get(data, field.section),
      sectionConfig = catalog.sectionConfigFor(field.section),
      sectionValue = _.last(columns)
    if (field.def?.type === 'String' && updatedValue === undefined) {
      return
    }
    const changes = nested.map(n => {
      if (_.get(n, sectionConfig.viewOptions.field) === sectionValue) {
        const f = field.field.replace(`${field.section}.`, ''),
          val = field.def?.type === 'Boolean' ? !_.get(n, f) : updatedValue
        _.set(n, f, val)
      }
      return n
    })

    clone[rowIndex] = Object.assign({}, clone[rowIndex], {...data, [field.section]: changes})

    this.data = clone

    if (!_.isArray(updateOnAssignment)) {

      const {expression: {field: f, assignTo, computeField}} = updateOnAssignment,
        d = _.get(data, section),
        fPath = computeField ?? field.field

      if (assignTo && d) {
        _.set(data, f, assignTo(Array.isArray(d) ? d.length === 0 ? null : d?.map(v => _.isNumber(_.get(v, fPath)) ? _.toNumber(_.get(v, fPath) ?? 0) : _.get(v, fPath)) : 0))
      }
    }

    await updateObjectInRemoteByQuery({
      clearLoader: true,
      queryItemIds: [id],
      toIndex, changes: this.getMutationsObject(data, prev), query, isMultiple: true, data
    })
  }

  @action renderPaginationToolbar = (paginationProps) => {
    return <PaginationToolbar {...paginationProps} />
  }

  @action onEditComplete = async ({value, columnId, rowIndex, data}: iTypeEditInfoExtended) => {
    const fieldName = _.first(columnId.split('_')),
      prev = _.cloneDeep(data),
      {catalog, es, catalog: {sidebar}} = this,
      fieldDef = catalog._fieldByName[fieldName] ?? catalog._fieldByName[columnId],
      {field, section, group, def, esIndex} = fieldDef,
      updatedValue = value ?? this.editValue,
      index = ((this.skip / this.limit) * this.limit) + rowIndex
    if (updatedValue === undefined) return

    if (section && !esIndex) {
      return await this._updateNestedData({rowIndex: index, updatedValue: value, columnId, prev: data})
    }
    if (value === es.readField(data, field, group?.field) && def?.type !== 'Boolean') return null
    const {index: toIndex, idFor, createItem: {updateObjectInRemoteByQuery}} = catalog,
      clone = [...this.data],
      id = idFor(data),
      query = `{"terms": {"exId": ${stringify([id])} }}`,
      newData = _.cloneDeep(clone[index])
    _.set(newData, field, updatedValue)
    clone.splice(index, 1, newData)
    this.data = clone
    if (!id.startsWith(OFFLINE_ID)) {
      this.editValue = null
      await updateObjectInRemoteByQuery({
        clearLoader: true,
        queryItemIds: [id],
        toIndex, changes: this.getMutationsObject(clone[index], prev), query, isMultiple: false
      })
      if (sidebar?.right?.details?.id === id) {
        await sidebar.right.details.loadData()
      }
      //iterate all stores, check if details pane is available then reload
      StoresInstance.allStores.forEach(store => {
        if (store.sidebar?.right?.details && store.sidebar?.right?.details?.id === id) {
          store.sidebar.right.details.loadData()
        }
      })
    } else {
      await catalog.upsertOfflineItem({index: catalog.index, value: {...newData, id: newData._id}})
    }
  }

  @action onRenderRow = (rowProps) => {
    const {onClick, onDoubleClick} = rowProps

    rowProps.onDoubleClick = (event) => {
      this.onRowDoubleClick(event, rowProps)
      if (onDoubleClick) {
        onDoubleClick(event)
      }
    }

    rowProps.onClick = (event) => {
      this.onRowClick(event, rowProps)
      if (onClick) {
        onClick(event)
      }
    }

    /*if (store) {
      const {
        dragDropEventHandlers: {
          events: {
            onDragStart, onDragEnd, onDragEnter, onDrag, onDragOver, onDragLeave, onDrop
          }
        }, onMouseEnter: storeOnMouseEnter, onMouseLeave: storeOnMouseLeave
      } = store

      rowProps.onDragStart = onDragStart
      rowProps.onDrop = onDrop
      rowProps.onDragEnd = onDragEnd
      rowProps.onDrag = onDrag
      rowProps.onDragOver = onDragOver
      rowProps.onDragLeave = onDragLeave

      rowProps.onMouseEnter = (e) => {
        console.log('onMouse enter')
        storeOnMouseEnter(e)
        if (onMouseEnter) {
          onMouseEnter(e)
        }
      }

      rowProps.onMouseEnter = (e) => {
        storeOnMouseLeave(e)
        if (onMouseLeave) {
          onMouseLeave(e)
        }
      }
    }*/
  }

  @action onEditValueChange = ({value, data}: TypeEditInfo & { data?: any }) => {
    this.editValue = value

    const {catalog} = this

    catalog.focusedCatalogItemId = catalog.idFor(data)
  }

  @action onDivClick = () => {
    // const id = catalog.idFor(data),
    //   builderCardStore = this.builderCardStoreDict.get(id)
    //
    // if (!builderCardStore) console.warn('builder card should not be empty', id)
    //
    // builderCardStore?.onDoubleClick_forTabNode(e)
  }

  @action onLimitChange = (total) => {
    /*let newLimit = this.limit + total
    newLimit = Math.min(newLimit, 100)
    newLimit = Math.max(newLimit, 0)*/
    this.limit = total
  }

  @action onSkipChange = (total) => {
    const {skip} = this
    let newSkip = skip + total
    newSkip = Math.min(newSkip, total)
    newSkip = Math.max(newSkip, 0)
    this.skip = newSkip
  }

  @action rowExpandChange = (rowChangeObj: any) => {
    const {expandedRowHeights, rootStore, entireRowExpand} = this,
      id = rowChangeObj.data?.id,
      {subColumns} = rootStore

    //set value if row is expanded or not
    entireRowExpand.set(id, rowChangeObj.expandedRows[id])

    if (expandedRowHeights.get(id)) {
      expandedRowHeights.delete(id)
      entireRowExpand.delete(id)
      if (rowChangeObj.expandedRows[id]) {
        // Expanded -> true
        expandedRowHeights.set(id, (subColumns.size * 250) + 250)
      } else {
        // Expanded -> false
        expandedRowHeights.set(id, 250)
      }
    }

    return true
  }

  @action rowClass = (rowData) => {
    const {expandedRowHeights} = this,
      id = rowData.data?.id

    // return customied class if inline image is expanded
    if (expandedRowHeights.get(id)) {
      return 'custom-row-height'
    } else {
      return ''
    }
  }

  @action updateTableViewDataByIndex = (items: any) => {
    const {catalog: {idFor}} = this
    const clone = [...this.data]
    items.forEach(item => {
      const id = idFor(item),
        index = clone.findIndex(i => idFor(i) === id)
      clone[index] = _.cloneDeep(item)
    })
    this.data = clone
  }

  @action onColumnResize = ({column, flex, width}) => {
    const {rootStore, rootStore: {columns}} = this
    rootStore.columns = columns.map(c => {
      if (c.name === column.name) {
        c = Object.assign({}, c, {width, flex})
      }
      return c
    })

  }

  @action onScroll = () => {
    const currentEl = this.ref?.current,
      scrollEls: NodeListOf<HTMLDivElement> = document.querySelectorAll('[data-name="scroller"]')
    if (currentEl?.scrollLeft) {
      scrollEls?.forEach(el => {
        el.scrollLeft = currentEl.scrollLeft
      })
    }

    const firstEl = _.first(scrollEls),
      {data, loading, everLoaded, catalog: {itemCount}} = this

    if (data.length && this.skip < data.length && data.length < itemCount && firstEl) {
      const scrollHeight = firstEl.scrollHeight - firstEl.offsetHeight
      if (!loading && scrollHeight > 0 && firstEl.scrollTop > 0 && (scrollHeight - firstEl.scrollTop) < 100) {
        this.skip += 50
      }
    }
  }

  @action toggleExpandDataGridRowById = (rowId: string) => {
    const {expandedRowHeights, rootStore, entireRowExpand} = this,
      {subColumns} = rootStore
    let lod = 0
    if (expandedRowHeights.get(rowId)) {
      if (entireRowExpand.get(rowId)) {
        const rowHght = expandedRowHeights.get(rowId)
        expandedRowHeights.delete(rowId)
        expandedRowHeights.set(rowId, rowHght - 200)
      } else {
        expandedRowHeights.delete(rowId)
      }
    } else {
      lod = 1
      if (entireRowExpand.get(rowId)) {
        expandedRowHeights.set(rowId, (subColumns.size * 250) + 250)
      } else {
        expandedRowHeights.set(rowId, 250)
      }
    }
    this.updateLocalData(rowId, {lod})
    expandedRowHeights.size !== 0 ? rootStore.setWidth(280) : rootStore.setWidth(100)
  }

  registerEffects = () => {
    // Prevent mouse horizontal scroll trigger back button
    /** https://stackoverflow.com/questions/12381563/how-can-i-stop-the-browser-back-button-using-javascript **/
    history.pushState(null, document.title, location.href)
    window.addEventListener('popstate', this._noBackButton)

    const {catalog, stores: {sidebar, es}} = this
    let first = true

    const toDispose = [
      autorun(() => {
        const {itemCount, settings: {showOnlyOfflineItems}, imageSource} = catalog, {searchPayload} = this
        // console.debug({itemCount, showOnlyOfflineItems, imageSource, searchPayload})
        this.key = guid()
      }, {name: 'Reset grid when data is stale'}),
      autorun(async () => {
        // if (!this.everLoaded) {
        const {itemCount, settings: {showOnlyOfflineItems}, imageSource} = catalog, {searchPayload} = this
        this.everLoaded = false
        // console.debug({itemCount, showOnlyOfflineItems, imageSource, searchPayload})
        await this._debounceRunQuery({skip: 0, limit: 50, force: true})
        // }
      }, {name: `Run grid query when data changes`}),
      autorun(async () => {
        const {skip, limit, prevLimit} = this
        // trace()
        await this.runQuery({skip, limit, force: limit !== prevLimit})
        this.prevLimit = limit
      }, {name: `Run grid query when paging changed`}),
      autorun(async () => {
        const {_tableRefreshDataByIds} = catalog,
          {right} = sidebar,
          sidebarData = right?.details?.data ?? {},
          isDirty = right?.details?.isDirty,
          isDiff = differenceInObject(sidebarData, this.prevSidebarData)
        if (_tableRefreshDataByIds.length || (!_.isEmpty(isDiff) && !isDirty)) {
          const offlineIds = _tableRefreshDataByIds.filter(id => id.startsWith(OFFLINE_ID)),
            exId = _tableRefreshDataByIds.length ? _.difference(_tableRefreshDataByIds, offlineIds) : [catalog.idFor(right?.details?.data)]
          if (exId.length) {
            setTimeout(async () => {
              const updatedData = await es.search({
                  meta: `Table Updated Data By Ids - ${_tableRefreshDataByIds.join(', ')}`,
                  body: {
                    query: {
                      terms: {
                        exId
                      }
                    },
                    size: exId.length,
                    ...catalog._sourceDynamic
                  }
                }),
                {items} = es.unwrapHitsResult(updatedData)
              this._debounceUpdateDataByIndex(items)
            }, 3000)
          }
          if (offlineIds.length) {
            const {getOfflineItem, index} = catalog,
              offlineItems = offlineIds.map(_id => {
                return getOfflineItem({index, _id})
              })
            this.updateTableViewDataByIndex(offlineItems)
          }
          if (_tableRefreshDataByIds.length && _tableRefreshDataByIds.includes(sidebar?.right?.details?.id)) {
            await sidebar.right.details.loadData()
          }
          catalog._tableRefreshDataByIds = []
          this.prevSidebarData = sidebarData
        }
      }, {name: 'Table view update on sidebar data update'}),
      autorun(() => {
        if (first) {
          const {expandedCatalogItems} = this
          expandedCatalogItems.forEach(item => {
            this.expandedRowHeights.set(item, 250)
          })
          this.expandedRowHeights.size !== 0 ? this.rootStore.setWidth(280) : this.rootStore.setWidth(100)
          first = false
        }
      }, {name: 'sync catalog expanded items'})
    ]
    return () => {
      window.removeEventListener('popstate', this._noBackButton)
      toDispose.forEach(d => d())
    }
  }

  private _dataLoading: boolean = false

  getMutationsObject = (data, prev) => {
    let diff = differenceInObject(data, prev)

    return _.keys(diff).reduce((acc, key) => {
      return {
        ...acc,
        [key]: {field: key, updatedValue: recursivelyRemovePrivateFields(_.cloneDeep(data[key]))}
      }
    }, {})
  }

  localData = computedFn((id) => {
    const {catalog: {settings}} = this
    return settings.perItem[id] ?? undefined
  })

  @action updateLocalData = (rowId: string, changes?: Partial<BuilderCardLocalData>) => {
    const {catalog, localData} = this
    catalog.settings.perItem[rowId] = _.merge(
      {},
      localData(rowId),
      changes
    )
  }

  // onActiveIndexChange =  (index) => {
  //   const {ref} = this,
  //   newIndex = ref.current.getItemId(ref.current.getItemAt(index))
  //   this.activeRowId = newIndex
  // } 

}




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_CatalogItems/DataGrid/DataGridStore.tsx